const cities = [
  { name: "Key Largo", zips: "33037" },
  { name: "Islamorada", zips: "33036" },
  { name: "Tavernier", zips: "33070" },
  { name: "Marathon", zips: "33050" },
  { name: "Duck Key", zips: "33050" },
  { name: "Key West", zips: "33040" },
  { name: "Big Pine Key", zips: "33043" },
  { name: "Florida City", zips: "33034" },
];

export const landmarks = [
  { name: "Miami International Airport (MIA)", distance: "~65 miles", icon: "ri-flight-takeoff-line", desc: "Closest major airport — north via Overseas Highway and Florida's Turnpike", url: "https://www.miami-airport.com/", schemaType: "Airport" },
  { name: "Key West International Airport (EYW)", distance: "~100 miles", icon: "ri-flight-takeoff-line", desc: "Alternative airport southwest — flights via American, Delta, Silver Airways", url: "https://www.eyw.com/", schemaType: "Airport" },
  { name: "Overseas Highway (US-1)", distance: "Adjacent", icon: "ri-road-map-line", desc: "Only road connecting the Florida Keys — enters Key Largo at Mile Marker 106 at Jewfish Creek Bridge", url: "https://en.wikipedia.org/wiki/U.S._Route_1_in_Florida", schemaType: "CivicStructure" },
  { name: "John Pennekamp Coral Reef State Park", distance: "~2 miles", icon: "ri-landscape-line", desc: "First underwater state park in the U.S. — 70 nautical square miles of coral reefs, mangroves, and seagrass", url: "https://www.floridastateparks.org/parks-and-trails/john-pennekamp-coral-reef-state-park", schemaType: "TouristAttraction" },
  { name: "Christ of the Abyss", distance: "~5 miles offshore", icon: "ri-anchor-line", desc: "Iconic 9-foot bronze statue in 25 feet of water at Dry Rocks reef — world-famous dive site", url: "https://floridakeys.noaa.gov/", schemaType: "TouristAttraction" },
  { name: "The African Queen", distance: "~3 miles", icon: "ri-ship-line", desc: "Original steamboat from 1951 Bogart/Hepburn film — canal cruises through mangrove waterways", url: "https://www.africanqueenflkeys.com/", schemaType: "TouristAttraction" },
  { name: "Dagny Johnson Botanical State Park", distance: "~5 miles", icon: "ri-leaf-line", desc: "Largest West Indian tropical hardwood hammock in the U.S. — 80+ protected species", url: "https://www.floridastateparks.org/parks-and-trails/dagny-johnson-key-largo-hammock-botanical-state-park", schemaType: "TouristAttraction" },
  { name: "MarineLab", distance: "~3 miles", icon: "ri-building-2-line", desc: "Marine science education and research center drawing students and researchers from across the globe", url: "https://www.marinelab.org/", schemaType: "TouristAttraction" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your Keys home, dive shop, or boat in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function KeyLargoLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Key Largo &amp; the Florida Keys
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Key Largo, Tavernier, Islamorada, Marathon, or anywhere in the Florida Keys, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Key Largo sits at the very threshold of the Florida Keys — the first island community you encounter after crossing the Jewfish Creek Bridge from mainland Florida at Mile Marker 106. But to call it merely an entry point would be a profound understatement. Key Largo is the "Diving Capital of the World," a title earned not through marketing but through decades of underwater exploration, marine conservation, and some of the most accessible and spectacular coral reef ecosystems on the planet. This is where the continental United States meets the living ocean, where the Gulf Stream's warm currents sweep across shallow reefs just a few miles offshore, and where the underwater world becomes as much a part of daily life as the tropical streets above.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What distinguishes Key Largo is John Pennekamp Coral Reef State Park, established in 1963 as the first underwater state park in the United States. Spanning approximately 70 nautical square miles, the park protects mangrove swamps, seagrass beds, and coral reefs that host more than 600 species of fish, sea turtles, and marine mammals. The park's centerpiece is the Christ of the Abyss, a nine-foot bronze statue of Jesus Christ standing in 25 feet of water near Dry Rocks reef — one of the most photographed underwater sites in the world and a pilgrimage destination for divers from every continent. Molasses Reef, French Reef, and The Elbow offer world-class wall dives, coral gardens, and swim-throughs that draw technical divers, underwater photographers, and marine biologists year-round.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Beyond diving, Key Largo offers a rich tapestry of marine experiences. The African Queen, the actual steamboat from the classic 1951 Humphrey Bogart and Katharine Hepburn film, operates canal cruises through the mangrove-lined waterways — a living piece of cinema history in an authentically tropical setting. The Dagny Johnson Key Largo Hammock Botanical State Park protects one of the largest tracts of West Indian tropical hardwood hammock in the United States, with more than 80 protected plant and animal species. Harry Harris Park provides a sheltered beach and swimming area for families. Kayak and paddleboard tours through the mangrove tunnels reveal manatees, dolphins, and wading birds in their natural habitat. This is not a manufactured resort destination — it is a working marine community where dive shops, charter boats, marine research stations, and conservation organizations form the economic backbone.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Key Largo's geographic position shapes its unique travel identity. At roughly 60 miles from Miami International Airport via the Overseas Highway, Key Largo is the most accessible of the Florida Keys — close enough for day trips from Miami, yet far enough to feel completely removed from urban Florida. The Everglades National Park borders Key Largo to the north, creating a continuous protected marine corridor from the mangrove estuaries of the 'Glades to the coral reefs of the Atlantic. This proximity to both wild Florida and international travel hubs makes Key Largo a natural staging ground for Caribbean and Central American diving expeditions, liveaboard trips, and marine research travel.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The passport challenges Key Largo residents and visitors face are directly tied to this marine-centric travel culture. Dive trips to Cozumel, the Bahamas, Belize, and the Cayman Islands are not once-in-a-lifetime vacations for many Key Largo regulars — they are annual or semi-annual commitments. Liveaboard dive boats depart from Miami and Fort Lauderdale for week-long excursions to the Bahamas and remote Caribbean reefs. Marine biologists and conservation researchers rotate between field stations in Key Largo, Bimini, Utila, and Roatan. Charter captains run multi-day diving and fishing expeditions to international waters. Each of these travel patterns requires a current passport, and each group discovers expiration issues at different points in their travel cycle.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            The Overseas Highway — U.S. Route 1 — connects Key Largo to the mainland, but it is the only road. When traffic backs up at the Jewfish Creek Bridge, during hurricane evacuations, or during peak season weekends, that 60-mile drive to Miami can stretch to two hours or more. For a Key Largo dive instructor who discovers an expired passport three days before a liveaboard departure to the Bahamas, driving to the Miami Passport Agency is not just inconvenient — it may mean missing a boat that departs on a fixed schedule. Fast Passport Center has processed expedited passports for Key Largo travelers across every marine demographic — dive instructors, underwater photographers, marine researchers, charter captains, seasonal residents, and families heading to Caribbean cruises. We understand that a diver flying MIA to Cozumel for a cenote expedition has the same urgency as a family of four leaving on a Bahamas liveaboard. Our courier-based expedited service means you start online from your Key Largo waterfront condo, your dive shop, or your favorite tiki bar — and your passport arrives within days.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex flex-col xl:flex-row gap-6">
                <div className="flex-1">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Key Largo — approximately 100 miles north via Overseas Highway / I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Monroe County and the Florida Keys</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or Key Largo marina in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

                {/* Map */}
                <div className="xl:w-[340px] flex-shrink-0">
                  <div className="rounded-lg overflow-hidden border border-white/10" style={{ height: '260px' }}>
                    <iframe
                      src="https://maps.google.com/maps?q=401+W+Atlantic+Ave+%23351,+Delray+Beach,+FL+33444&t=&z=15&ie=UTF8&iwloc=&output=embed"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Fast Passport Center Delray Beach Office Location"
                      aria-label="Google Map showing Fast Passport Center Delray Beach office at 401 W Atlantic Ave Suite 351"
                    />
                  </div>
                  <p className="text-white/40 text-xs mt-2 text-center">
                    401 W Atlantic Ave #351, Delray Beach, FL 33444
                  </p>
                </div>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Monroe County and the Florida Keys — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Key West", href: "/key-west" },
                  { name: "Islamorada", href: "/islamorada" },
                  { name: "Miami", href: "/miami" },
                  { name: "Marathon", href: "/florida" },
                  { name: "Naples", href: "/naples" },
                  { name: "Fort Myers", href: "/fort-myers" },
                  { name: "Miami", href: "/aventura" },
                  { name: "Fort Myers", href: "/fort-myers" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Nearby Landmarks & Airports */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15">
                  <i className="ri-landscape-line text-[#1A365D] text-xs"></i>
                </div>
                <h3 className="font-semibold text-[#1A365D] text-sm uppercase tracking-wide">Nearby Landmarks &amp; Airports</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {landmarks.map((item) => (
                  <span
                    key={item.name}
                    className="inline-flex items-center gap-1.5 bg-[#F7F8FA] border border-gray-200/80 rounded-full px-3 py-1.5 hover:border-[#F6C344] transition-colors cursor-default whitespace-nowrap"
                    title={item.desc}
                  >
                    <i className={`${item.icon} text-[#1A365D] text-[10px]`}></i>
                    <span className="text-[#1A365D] font-medium text-[11px]">{item.name}</span>
                    <span className="text-[#F6C344] font-semibold text-[10px]">{item.distance}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}